import { useCallback, useEffect, useState } from "react"
import {
  type GiuratoInGruppo,
  type Gruppo as DatiGruppo,
  type StatoRegia,
  apiRegia,
} from "../../api/regia"
import { useLargo } from "../../stato/useLargo"

interface Props {
  stato: StatoRegia
  codice: string
  comando: (azione: () => Promise<unknown>) => Promise<void>
}

/** Qualcuno può iscriversi proprio mentre guardi: si rilegge ogni tanto.
 *  I campi aperti non si perdono, perché ogni persona tiene il suo stato. */
const RINFRESCO_MS = 10_000

type Agisci = (azione: () => Promise<unknown>) => Promise<void>

export function Gruppo({ stato, codice, comando }: Props) {
  const [gruppo, setGruppo] = useState<DatiGruppo | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const largo = useLargo()
  const editionId = stato.edizione.id

  const carica = useCallback(async () => {
    try {
      setGruppo(await apiRegia.gruppo(editionId, codice))
      setErrore(null)
    } catch (e) {
      setErrore(
        e instanceof Error
          ? `Non riesco a leggere il gruppo: ${e.message}`
          : "Non riesco a leggere il gruppo.",
      )
    }
  }, [editionId, codice])

  useEffect(() => {
    void carica()
    const t = setInterval(() => void carica(), RINFRESCO_MS)
    return () => clearInterval(t)
  }, [carica])

  /** Ogni azione passa dal `comando` della regia, che mostra gli errori in
   *  alto; poi si rilegge il gruppo. */
  const agisci = useCallback<Agisci>(
    (azione) =>
      comando(async () => {
        await azione()
        await carica()
      }),
    [comando, carica],
  )

  if (!gruppo) {
    return (
      <section className="gruppo">
        <p className="occhiello">Gruppo</p>
        {errore ? <p className="avviso">{errore}</p> : <p>Carico…</p>}
      </section>
    )
  }

  const link = `${location.origin}/entra/${gruppo.join_code}`
  const quante = gruppo.giurati.length

  return (
    <section className="gruppo">
      <p className="occhiello">
        {quante} {quante === 1 ? "persona" : "persone"} in {stato.edizione.name}
      </p>

      {/* Un rinfresco può fallire dopo il primo caricamento riuscito: si
          avvisa qui, tenendo comunque visibili gli ultimi dati buoni. */}
      {errore && <p className="avviso">{errore}</p>}

      {/* Al telefono il link viene prima: è la prima cosa che serve. Sul
          portatile sta nella colonna di destra, come i moduli di Artisti. */}
      <div className="gruppo__laterale">
        <div className="scheda gruppo__invito">
          <p className="occhiello">Link d'invito</p>
          <code className="gruppo__link">{link}</code>
          <Condividi link={link} testo={`Entra nella ${stato.edizione.name}:`} />
          <p className="delicate__nota">Chi lo apre si crea l'account ed è subito nel gruppo.</p>
          <details className="delicate">
            <summary>Rigenera il link</summary>
            <p className="delicate__nota">
              Il vecchio link smette di funzionare. Chi è già nel gruppo non viene toccato.
            </p>
            <button
              type="button"
              className="bottone delicate__rossa"
              onClick={() => void agisci(() => apiRegia.rigeneraLink(editionId, codice))}
            >
              Sì, rigenera il link
            </button>
          </details>
        </div>
      </div>

      {quante === 0 ? (
        <p className="regia__vuoto gruppo__elenco">
          Non c'è ancora nessuno. Manda il link {largo ? "qui accanto" : "qui sopra"} su WhatsApp.
        </p>
      ) : (
        <ul className="gruppo__elenco">
          {gruppo.giurati.map((g) => (
            <Persona key={g.id} giurato={g} codice={codice} agisci={agisci} />
          ))}
        </ul>
      )}
    </section>
  )
}

function Condividi({ link, testo }: { link: string; testo: string }) {
  const [copiato, setCopiato] = useState(false)
  // Sul telefono apre il foglio di condivisione (WhatsApp in un tocco);
  // sul Mac di solito non c'è, e resta Copia.
  const puoCondividere = typeof navigator.share === "function"

  async function copia() {
    try {
      await navigator.clipboard.writeText(link)
      setCopiato(true)
      setTimeout(() => setCopiato(false), 2000)
    } catch {
      /* niente appunti (permessi, http): il link resta selezionabile a mano */
    }
  }

  return (
    <div className="gruppo__condividi">
      {puoCondividere && (
        <button
          type="button"
          className="bottone primario"
          onClick={() => void navigator.share({ text: testo, url: link }).catch(() => undefined)}
        >
          Condividi
        </button>
      )}
      <button type="button" className="bottone" onClick={() => void copia()}>
        {copiato ? "Copiato" : "Copia"}
      </button>
    </div>
  )
}

function giorno(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" })
}

function orario(iso: string): string {
  return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
}

/** "online" e non "visto": i testi non devono indovinare il genere. */
function online(iso: string | null): string {
  if (!iso) return "mai online"
  const minuti = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minuti < 1) return "online ora"
  if (minuti < 60) return `online ${minuti} min fa`
  const ore = Math.round(minuti / 60)
  if (ore < 24) return `online ${ore} ${ore === 1 ? "ora" : "ore"} fa`
  return `online il ${giorno(iso)}`
}

function dettagli(g: GiuratoInGruppo): string {
  return [g.joined_at && `nel gruppo dal ${giorno(g.joined_at)}`, online(g.last_seen_at)]
    .filter(Boolean)
    .join(" · ")
}

function Persona({
  giurato,
  codice,
  agisci,
}: {
  giurato: GiuratoInGruppo
  codice: string
  agisci: Agisci
}) {
  const [aperto, setAperto] = useState(false)
  const [nome, setNome] = useState(giurato.nickname)
  const [reset, setReset] = useState<{ link: string; scade: string } | null>(null)
  const [conferma, setConferma] = useState(false)

  const togli = (fuori: boolean) =>
    agisci(() => apiRegia.modificaGiurato(giurato.id, { excluded: fuori }, codice))

  return (
    <li className="gruppo__voce" data-fuori={giurato.excluded}>
      <div className="gruppo__riga">
        <span className="gruppo__emoji" aria-hidden="true">
          {giurato.emoji}
        </span>
        <span className="regia__nome">
          {giurato.nickname}
          <small>{dettagli(giurato)}</small>
          {giurato.excluded && <span className="gruppo__segno">fuori dal gruppo</span>}
          {giurato.locked_until && (
            <span className="gruppo__segno gruppo__segno--bloccato">
              accesso bloccato fino alle {orario(giurato.locked_until)}
            </span>
          )}
        </span>
        <button
          type="button"
          className="bottone artisti__modifica"
          aria-expanded={aperto}
          onClick={() => setAperto((a) => !a)}
        >
          {aperto ? "Chiudi" : "Gestisci"}
        </button>
      </div>

      {aperto && (
        <div className="gruppo__gestione">
          <form
            className="gruppo__rinomina"
            onSubmit={(ev) => {
              ev.preventDefault()
              void agisci(() =>
                apiRegia.modificaGiurato(giurato.id, { nickname: nome.trim() }, codice),
              )
            }}
          >
            <label className="edizioni__campo">
              Nome
              <input
                className="campo"
                value={nome}
                maxLength={40}
                onChange={(e) => setNome(e.target.value)}
              />
            </label>
            <button
              className="bottone"
              type="submit"
              disabled={!nome.trim() || nome.trim() === giurato.nickname}
            >
              Rinomina
            </button>
          </form>

          <div className="gruppo__invito">
            <button
              type="button"
              className="bottone"
              onClick={() =>
                void agisci(async () => {
                  const r = await apiRegia.linkReset(giurato.id, codice)
                  setReset({ link: `${location.origin}/reset/${r.codice}`, scade: r.scade })
                })
              }
            >
              {reset ? "Genera un altro link" : "Link per una nuova password"}
            </button>
            {reset && (
              <>
                <code className="gruppo__link">{reset.link}</code>
                <Condividi
                  link={reset.link}
                  testo={`${giurato.nickname}, da qui scegli la nuova password della Giuria Trash:`}
                />
                <p className="delicate__nota">
                  Vale una volta sola, fino a domani alle {orario(reset.scade)}. Il link
                  precedente non funziona più.
                </p>
              </>
            )}
          </div>

          {giurato.excluded ? (
            <button type="button" className="bottone" onClick={() => void togli(false)}>
              Riammetti {giurato.nickname}
            </button>
          ) : conferma ? (
            <div className="delicate__conferma">
              <button type="button" className="bottone" onClick={() => setConferma(false)}>
                No
              </button>
              <button
                type="button"
                className="bottone delicate__rossa"
                onClick={() => {
                  setConferma(false)
                  void togli(true)
                }}
              >
                Sì, togli {giurato.nickname} dal gruppo
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="bottone" onClick={() => setConferma(true)}>
                Togli dal gruppo
              </button>
              <p className="delicate__nota">
                Non potrà più entrare in questa edizione, nemmeno con la password. I voti già dati
                restano. Se ha ancora il link d'invito può crearsi un altro account: in quel caso
                rigenera il link.
              </p>
            </>
          )}
        </div>
      )}
    </li>
  )
}
